import React, { useState, useEffect } from "react";
import HorizontalProgressChart from "common-ui-components/components/horizontal-progress-bar/HorizontalProgressChart";
import API from "common-ui-components/components/utils/API";
import responseHandler from "app/manage/software-repository/utils/responseHandler";
import { unstable_batchedUpdates } from "react-dom";

const SEVERITY_CONFIG = {
  CRITICAL: { label: "Critical", color: "#e0432a" },
  HIGH: { label: "High", color: "#c026b8" }, // Replaces "Important"
  MEDIUM: { label: "Medium", color: "#2f6fed" }, // Replaces "Moderate"
  LOW: { label: "Low", color: "#0e9384" },
};

// Define the desired order of severities
const SEVERITY_ORDER = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];

const SeverityBreakdownChart = ({
  setVulnerabilitiesCount,
  onSeveritySelect,
}) => {
  const [dataItems, setDataItems] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSeverityData = async () => {
      try {
        setIsLoading(true);
        const response = await API.get("/vulnerabilities/severity-graph");
        const formattedResponse = responseHandler(response);

        if (!formattedResponse.ok) throw formattedResponse;

        const rawData = formattedResponse?.body || [];
        const formattedData = {};
        let totalVulnerabilities = 0;

        // 1. Process severities in the defined SEVERITY_ORDER
        SEVERITY_ORDER.forEach((severityKey) => {
          const config = SEVERITY_CONFIG[severityKey];
          if (config) {
            const matchingDataItem = rawData.find(
              (item) => item.severity.toUpperCase() === severityKey,
            );
            const count = matchingDataItem ? matchingDataItem.count : 0;

            formattedData[config.label] = {
              label: config.label,
              value: count,
              color: config.color,
            };
            totalVulnerabilities += count;
          }
        });

        // 2. Add any severities from rawData that were not in SEVERITY_ORDER
        // This handles cases where the backend might return new severity levels
        rawData.forEach((item) => {
          const key = item.severity.toUpperCase();
          if (!SEVERITY_ORDER.includes(key)) {
            const config = {
              label: item.severity,
              color: "#9ca3af", // Default color for unknown severities
            };
            // Only add if a severity with this label hasn't been added yet
            if (!formattedData[config.label]) {
              formattedData[config.label] = {
                label: config.label,
                value: item.count,
                color: config.color,
              };
              totalVulnerabilities += item.count;
            }
          }
        });

        unstable_batchedUpdates(() => {
          setDataItems(formattedData);
          setVulnerabilitiesCount(totalVulnerabilities);
        });
      } catch (error) {
        console.error("Failed to fetch severity graph data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSeverityData();
  }, []);

  return (
    <HorizontalProgressChart
      title="Severity Breakdown"
      summaryText="total open"
      dataItems={dataItems}
      isLoading={isLoading}
      onItemClick={({ label }) => onSeveritySelect?.(label)}
    />
  );
};

export default SeverityBreakdownChart;
